import { useSelector } from 'react-redux';
import { keepPreviousData } from '@tanstack/react-query';
import { useKnowledgeItemsById } from './knowledge-items.hooks';
import {
  getFavoriteKnowledgeItemIds,
  getFavoritesItems,
} from '../store/selectors/favorites.selector';
import { getSelectedWorkspaceId } from '../store/selectors/workspace.selector';

interface FavoritesState {
  favorites: { byId: Record<string, { itemId: string }>; lastUpdated: number | null };
  contacts: unknown;
  usersManagement: unknown;
  workspace: { selected?: string; list?: { id: string }[] };
}

/**
 * The favorites list for the Favorites page and screen: contacts resolve from
 * the store, knowledge rows are fetched by id. A toggle changes the id set, and
 * the previous rows stay on screen until the new set lands.
 *
 * @returns `list` (contacts and knowledge rows, each with its `entity`), `isLoading`
 *   (the first row fetch is in flight) and the store's `lastUpdated` stamp.
 * @example
 * const { list, isLoading } = useFavoriteItems();
 * return <FavoritesPageComponent lang={lang} list={list} loading={isLoading} />;
 */
export function useFavoriteItems() {
  const favorites = useSelector((state: FavoritesState) => state.favorites);
  const contacts = useSelector((state: FavoritesState) => state.contacts);
  const usersManagement = useSelector((state: FavoritesState) => state.usersManagement);
  // Read through the store, not workspaceService, so a workspace switch re-renders.
  const workspaceId = useSelector((state: FavoritesState) => getSelectedWorkspaceId(state));
  const { itemsById, isLoading } = useKnowledgeItemsById(
    getFavoriteKnowledgeItemIds(favorites, workspaceId),
    { placeholderData: keepPreviousData },
  );

  const list = getFavoritesItems(
    { favorites, knowledge: { itemsById }, contacts, usersManagement },
    workspaceId,
  );

  return { list, isLoading, lastUpdated: favorites.lastUpdated };
}
